import { CRISIS_HTTP_STATUS } from '@tarot/shared';
import { clearIdentity, clearSessionToken, identityHeaders, setSessionToken } from './identity';
import { TOSS_EVENTS, tossLog } from './toss';
import type {
  AuthProvider,
  CreateRoomRequest,
  InterpretRequest,
  Mbti,
  ShareReadingRequest,
  QuotaStatus,
  SharedReading,
  ThemeName,
} from '@tarot/shared';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * API 응답이 JSON인지 확인한다.
 * /api/* 가 Worker 에 닿지 못하면 Pages 의 SPA 폴백이 200 + index.html 을 돌려준다.
 * 그대로 두면 파싱만 실패하고 화면은 "그냥 로그아웃 상태"로 보여 원인을 찾기 어렵다
 * (2026-08-19 www 도메인에서 실제 발생 — public/_redirects 참고).
 */
async function ensureJson(res: Response, label: string) {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json'))
    throw new Error(`${label} → JSON이 아닌 응답 (${res.status} ${type || '타입 없음'})`);
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: identityHeaders(),
  });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  await ensureJson(res, `GET ${path}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...identityHeaders() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  // 위기 발화 — 해석/방 생성을 하지 않고 상담 안내로 되돌린다 (shared safety.ts)
  if (res.status === CRISIS_HTTP_STATUS) throw new CrisisBlockedError();
  if (res.status === 429 || res.status === 402) {
    const b = (await res.json().catch(() => ({}))) as {
      error?: string;
      reason?: 'quota' | 'ad';
      quota?: QuotaStatus;
    };
    throw new QuotaExceededError(
      b.error ?? '오늘 볼 수 있는 횟수를 모두 사용했어요.',
      b.quota,
      b.reason,
    );
  }
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  await ensureJson(res, `POST ${path}`);
  return res.json() as Promise<T>;
}

/** 방 생성 → { roomId, inviteCode } */
export const createRoom = (body: CreateRoomRequest) =>
  apiPost<{ roomId: string; inviteCode: string }>('/rooms', body);

/** 초대코드 → roomId (없거나 만료 시 throw) */
export const resolveInviteCode = (code: string) =>
  apiGet<{ roomId: string }>(`/rooms/by-code/${encodeURIComponent(code)}`);

/**
 * 방이 아직 살아 있는지. `false` = 만료·삭제, `null` = **확인 실패(모름)**.
 *
 * 둘을 구분하는 게 요점이다. 재연결을 멈출지 판단할 때 쓰는데, 네트워크가 잠깐 끊겨서
 * 확인이 실패한 걸 "만료"로 처리해 버리면 멀쩡한 방에서 사람을 내보내게 된다.
 */
export async function roomAlive(roomId: string): Promise<boolean | null> {
  try {
    const res = await fetch(`${BASE}/rooms/${encodeURIComponent(roomId)}`, {
      credentials: 'include',
      headers: identityHeaders(),
    });
    if (res.status === 404) return false;
    return res.ok ? true : null;
  } catch {
    return null; // 오프라인 — 방이 없어졌다고 단정할 수 없다
  }
}

/* ── 인증 (로그인 = 히스토리 + 닉네임 관리, 비회원도 전 기능 사용 가능) ── */

export interface Me {
  id: string;
  provider: AuthProvider;
  nickname: string;
  theme: ThemeName;
  /** 선택 입력 — 미설정이면 빈 문자열 */
  mbti: Mbti | '';
}

export const getMe = () => apiGet<{ user: Me | null }>('/auth/me');
export async function logout(): Promise<{ ok: true }> {
  try {
    return await apiPost<{ ok: true }>('/auth/logout', {});
  } finally {
    // 미니앱 세션은 쿠키가 아니라 우리 손에 있다 — 서버 응답과 무관하게 지운다
    clearSessionToken();
  }
}

/**
 * 토스 로그인 (미니앱 전용).
 * SDK가 준 인가 코드를 서버가 검증하고, 돌려받은 토큰을 저장한다.
 */
export async function tossLogin(payload: {
  authorizationCode: string;
  referrer: string;
}): Promise<{ isNewUser: boolean; nickname: string }> {
  const res = await apiPost<{ token: string; isNewUser: boolean; nickname: string }>(
    '/auth/toss/login',
    payload,
  );
  setSessionToken(res.token);
  return { isNewUser: res.isNewUser, nickname: res.nickname };
}

/** 회원탈퇴 — 식별키·닉네임·저장 리딩 즉시 파기 (복구 불가) */
export async function deleteAccount(): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/auth/me`, {
    method: 'DELETE',
    credentials: 'include',
    headers: identityHeaders(),
  });
  if (!res.ok) throw new Error(`탈퇴 실패 (${res.status})`);
  /**
   * 헤더 세션은 우리 손에 있다 — 서버가 지울 수 없다.
   * 안 버리면 탈퇴한 계정의 토큰이 만료(7일)까지 계속 인증돼, 지운 계정으로 리딩이
   * 저장되고 회원 기준 횟수가 유지된다.
   */
  clearIdentity();
  return res.json();
}

export async function updateNickname(nickname: string): Promise<{ ok: true; nickname: string }> {
  const res = await fetch(`${BASE}/auth/me/nickname`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', ...identityHeaders() },
    credentials: 'include',
    body: JSON.stringify({ nickname }),
  });
  if (!res.ok) throw new Error(`닉네임 변경 실패 (${res.status})`);
  return res.json();
}

/** 화면 테마 저장 — DB(users.theme)에 저장되어 기기·브라우저가 바뀌어도 유지 */
export async function updateTheme(theme: ThemeName): Promise<{ ok: true; theme: ThemeName }> {
  const res = await fetch(`${BASE}/auth/me/theme`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', ...identityHeaders() },
    credentials: 'include',
    body: JSON.stringify({ theme }),
  });
  if (!res.ok) throw new Error(`테마 변경 실패 (${res.status})`);
  return res.json();
}

/** MBTI 저장 — 선택 입력(빈 문자열이면 해제). 해석 말투 개인화에만 사용 */
export async function updateMbti(mbti: Mbti | ''): Promise<{ ok: true; mbti: Mbti | '' }> {
  const res = await fetch(`${BASE}/auth/me/mbti`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', ...identityHeaders() },
    credentials: 'include',
    body: JSON.stringify({ mbti }),
  });
  if (!res.ok) throw new Error(`MBTI 변경 실패 (${res.status})`);
  return res.json();
}

/* ── 하루 이용 횟수 (shared quota.ts 정책) ── */

export const getQuota = () => apiGet<QuotaStatus>('/quota');

/** 광고 시청 보상 — 시청 완료 후 호출 */
export const claimAdReward = () =>
  apiPost<{ ok: true; status: QuotaStatus }>('/quota/ad-reward', {});

/** 한도 초과(429) — 남은 횟수 안내를 위해 상태를 함께 들고 다닌다 */
/**
 * 위기 발화가 감지돼 해석이 되돌아온 경우.
 *
 * 메시지를 담지 않는 이유: 화면에 뜨는 건 서버 문구가 아니라 `CrisisNotice`(상담 창구 포함)다.
 * 이 에러는 "그 화면을 띄우라"는 신호일 뿐이다. 횟수는 차감되지 않았다.
 */
export class CrisisBlockedError extends Error {
  constructor() {
    super('crisis');
    this.name = 'CrisisBlockedError';
  }
}

export class QuotaExceededError extends Error {
  constructor(
    message: string,
    readonly status?: QuotaStatus,
    /** 'ad' = 광고를 보면 계속 가능 / 'quota' = 하루 상한 도달 */
    readonly reason?: 'quota' | 'ad',
  ) {
    super(message);
    this.name = 'QuotaExceededError';
  }
}

export interface HistoryItem {
  id: string;
  mode: string;
  topic: string;
  spread: string;
  createdAt: number;
  /** 참여자 닉네임 (좌석 순) — 커플·우정에서 함께한 사람 표시용 */
  nicknames: string[];
}

/** 회원 히스토리 — 내가 발행한 리딩 (401 = 미로그인) */
export const listMyReadings = () => apiGet<{ items: HistoryItem[] }>('/readings');

/** 결과를 공유 링크로 발행 → shareId (웹 경로 /r/:shareId) */
export const shareReading = (body: ShareReadingRequest) =>
  apiPost<{ shareId: string }>('/readings/share', body);

/** 공유된 결과 조회 */
export const getSharedReading = (shareId: string) =>
  apiGet<SharedReading>(`/readings/shared/${encodeURIComponent(shareId)}`);

export interface InterpretHandlers {
  onDelta: (text: string) => void;
  onDone?: (meta: { model: string; tokensIn?: number; tokensOut?: number }) => void;
  onError?: (message: string) => void;
}

/**
 * POST /api/ai/interpret 를 SSE로 소비.
 * fetch + ReadableStream 파서 (EventSource는 POST 불가).
 */
export async function streamInterpret(
  body: InterpretRequest,
  h: InterpretHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}/ai/interpret`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...identityHeaders() },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  // 위기 발화 — 해석/방 생성을 하지 않고 상담 안내로 되돌린다 (shared safety.ts)
  if (res.status === CRISIS_HTTP_STATUS) throw new CrisisBlockedError();
  // 하루 이용 횟수 초과 — 남은 횟수 안내를 위해 전용 에러로 승격
  if (res.status === 429 || res.status === 402) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      reason?: 'quota' | 'ad';
      quota?: QuotaStatus;
    };
    throw new QuotaExceededError(
      body.error ?? '오늘 볼 수 있는 횟수를 모두 사용했어요.',
      body.quota,
      body.reason,
    );
  }
  if (!res.ok || !res.body) {
    h.onError?.(`요청 실패 (${res.status})`);
    return;
  }

  /**
   * 해석 완료를 토스 분석으로도 보낸다 — **대표 전환 지표의 근거**.
   * 여기서 감싸는 이유: 혼자·커플·우정 모든 모드가 이 함수를 지나므로 한 곳이면 충분하다.
   * 서버 통계(interpret:*)는 그대로 두고, 미니앱 노출 최적화용으로만 덧붙인다.
   */
  const handlers: InterpretHandlers = {
    ...h,
    onDone: (meta) => {
      tossLog(TOSS_EVENTS.readingComplete, {
        mode: body.mode,
        topic: body.topic,
        people: body.participants.length,
      });
      h.onDone?.(meta);
    },
  };

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      handleEvent(chunk, handlers);
    }
  }
}

function handleEvent(chunk: string, h: InterpretHandlers) {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of chunk.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  if (!dataLines.length) return;
  const data = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
  if (event === 'delta') h.onDelta(String(data.text ?? ''));
  else if (event === 'done')
    h.onDone?.(data as { model: string; tokensIn?: number; tokensOut?: number });
  else if (event === 'error') h.onError?.(String(data.message ?? 'error'));
}
